interface Vector2D {
  x: number;
  y: number;
}

interface Particle {
  size: Vector2D;
  position: Vector2D;
  velocity: Vector2D;
  rotation: number;
  rotation_speed: number;
  hue: number;
  opacity: number;
  lifetime: number;
  update(dt: number): void;
  checkBounds(): boolean;
  draw(): void;
}

interface ConfettiConfig {
  gravity: number;
  particle_count: number;
  particle_size: number;
  explosion_power: number;
  destroy_target: boolean;
  fade: boolean;
}

// --- CONFIGURATION ---
const CONFIG: ConfettiConfig = {
  gravity: 10,
  particle_count: 75,
  particle_size: 1,
  explosion_power: 25,
  destroy_target: false, // Hides the element after explosion.
  fade: false, // Set to true if you want particles to fade out.
};

export function showConfetti(el: HTMLElement): void {
  // --- SETUP CANVAS ---
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  // We use a 2x scaling factor (as in your original code) for higher resolution.
  canvas.width = 2 * window.innerWidth;
  canvas.height = 2 * window.innerHeight;
  canvas.style.position = "fixed";
  canvas.style.top = "0";
  canvas.style.left = "0";
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.margin = "0";
  canvas.style.padding = "0";
  canvas.style.zIndex = "999999999";
  canvas.style.pointerEvents = "none";
  document.body.appendChild(canvas);

  // --- VECTOR UTILITY ---
  function Vector(x: number = 0, y: number = 0): Vector2D {
    return { x, y };
  }

  // --- VELOCITY GENERATOR ---
  function generateVelocity(): Vector2D {
    // Generate random numbers for direction.
    let t = Math.random() - 0.5;
    let i = Math.random() - 0.7;
    let mag = Math.sqrt(t * t + i * i);
    // Normalize and scale by a random factor.
    t = (t / mag) * (Math.random() * CONFIG.explosion_power);
    i = (i / mag) * (Math.random() * CONFIG.explosion_power);
    return Vector(t, i);
  }

  // --- PARTICLE DEFINITION ---
  function ParticleFactory(): Particle {
    return {
      // Size is randomized as in the original code.
      size: Vector((16 * Math.random() + 4) * CONFIG.particle_size, (4 * Math.random() + 4) * CONFIG.particle_size),
      position: Vector(0, 0), // Will be set after size is calculated
      velocity: generateVelocity(),
      rotation: 360 * Math.random(),
      rotation_speed: 10 * (Math.random() - 0.5),
      hue: 360 * Math.random(),
      opacity: 100, // expressed as a percentage
      lifetime: Math.random() + 0.25,

      update(dt: number): void {
        // Apply gravity proportional to the particle's size.
        this.velocity.y += CONFIG.gravity * (this.size.y / (10 * CONFIG.particle_size)) * dt;
        // Add a slight random horizontal shift.
        this.velocity.x += 25 * (Math.random() - 0.5) * dt;
        // Apply some drag.
        this.velocity.x *= 0.98;
        this.velocity.y *= 0.98;
        // Update position.
        this.position.x += this.velocity.x;
        this.position.y += this.velocity.y;
        // Update rotation.
        this.rotation += this.rotation_speed;
        // Optionally fade the particle.
        if (CONFIG.fade) {
          this.opacity -= this.lifetime;
        }
      },

      checkBounds(): boolean {
        // Remove particle if it goes below the visible area.
        return this.position.y - 2 * this.size.x > 2 * window.innerHeight;
      },

      draw(): void {
        drawRectangle(this.position, this.size, this.rotation, this.hue, this.opacity);
      },
    };
  }

  // --- RENDERER FUNCTIONS ---
  function clearScreen(): void {
    ctx.clearRect(0, 0, 2 * window.innerWidth, 2 * window.innerHeight);
  }

  function drawRectangle(position: Vector2D, size: Vector2D, rotation: number, hue: number, opacity: number): void {
    ctx.save();
    ctx.beginPath();
    // Translate to the center of the particle.
    ctx.translate(position.x + size.x / 2, position.y + size.y / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.rect(-size.x / 2, -size.y / 2, size.x, size.y);
    ctx.fillStyle = `hsla(${hue}deg, 90%, 65%, ${opacity}%)`;
    ctx.fill();
    ctx.restore();
  }

  // --- SETUP BURST ---
  // Use the center of the provided element as the explosion origin.
  const elRect = el.getBoundingClientRect();
  // Multiply by 2 since the canvas is sized to 2 * innerWidth/innerHeight.
  const origin = Vector(2 * (elRect.left + elRect.width / 2), 2 * (elRect.top + elRect.height / 2));
  // Create an array of particles.
  const particles: Particle[] = [];
  for (let i = 0; i < CONFIG.particle_count; i++) {
    const p = ParticleFactory();
    p.position = Vector(origin.x - p.size.x / 2, origin.y - p.size.y / 2);
    particles.push(p);
  }

  // Optionally hide the target element.
  if (CONFIG.destroy_target) {
    el.style.visibility = "hidden";
  }

  // --- ANIMATION LOOP ---
  let lastTime = performance.now();

  function update(time: number): void {
    const dt = (time - lastTime) / 1000;
    lastTime = time;
    clearScreen();
    // Update and draw each particle.
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.update(dt);
      p.draw();
      if (p.checkBounds()) {
        particles.splice(i, 1);
      }
    }
    // Continue animation while there are particles.
    if (particles.length > 0) {
      requestAnimationFrame(update);
    } else {
      // Clean up: remove the canvas from the DOM.
      document.body.removeChild(canvas);
    }
  }
  requestAnimationFrame(update);
}
